import { X } from "lucide-react";
import { OriginPill } from "@/components/patterns/OriginPill";
import { Button, IconButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "@/components/ui/StatusPill";
import { SOURCE_LINK } from "@/components/ui/marks";
import { cn } from "@/lib/utils";
import { AddField } from "./AddField";
import { InlineLine } from "./InlineLine";
import { TEXT_LIMITS } from "@/lib/text-limits";

/** What a Praxis item rests on, as its row cites it: "Disclosure document 1, p.4", "Claim 3". */
export interface ChildSource {
  label: string;
  /** Opens the source where it is read; a source with no reader is cited in words only. */
  onOpen?: () => void;
}

/** One child item of a subsection: a key, its words if it has them, who wrote it, its source. */
export interface ChildLine {
  item_id: string;
  key: string;
  text?: string | null;
  origin: "praxis" | "attorney";
  source?: ChildSource;
  /** Set for an item stated only once the attorney accepts it: a range ladder's rung. */
  accepted?: boolean;
  /** A word about this item alone: "basis not stated", "outside claim 6's range". */
  note?: { words: string; tone: "warning" | "neutral" };
}

/** A key and its words as the attorney typed them; a key-only item has no words. */
export interface ChildWords {
  key: string;
  text?: string;
}

function Source({ source }: { source: ChildSource }) {
  // A long citation wraps within its own width rather than taking the words' room.
  const words = "max-w-64 shrink-0 text-right text-xs text-muted-foreground";
  if (source.onOpen === undefined)
    return <span className={words}>{source.label}</span>;
  return (
    <Button
      variant="link"
      size="dense"
      className={cn(words, SOURCE_LINK)}
      onClick={source.onOpen}
    >
      {source.label}
    </Button>
  );
}

function Item({
  item,
  keyOnly,
  readOnly,
  onReword,
  onDrop,
  onAccept,
}: {
  item: ChildLine;
  keyOnly: boolean;
  readOnly: boolean;
  onReword: (words: ChildWords, putBack: () => void) => void;
  onDrop: () => void;
  onAccept: (accepted: boolean) => void;
}) {
  const text = item.text ?? "";
  return (
    <li className="flex items-start gap-2 text-sm">
      {item.accepted !== undefined && (
        <Checkbox
          checked={item.accepted}
          disabled={readOnly}
          aria-label={`Accept "${item.key}"`}
          onCheckedChange={(checked) => {
            onAccept(checked === true);
          }}
        />
      )}
      <span className={keyOnly ? "min-w-0 flex-1" : "w-48 flex-none"}>
        <InlineLine
          value={item.key}
          label={`Key of "${item.key}"`}
          readOnly={readOnly}
          maxLength={TEXT_LIMITS.childKey}
          className="font-semibold"
          onSave={(key, putBack) => {
            onReword(keyOnly ? { key } : { key, text }, putBack);
          }}
        />
      </span>
      {!keyOnly && (
        <span className="min-w-0 flex-1">
          <InlineLine
            value={text}
            label={`Words of "${item.key}"`}
            readOnly={readOnly}
            maxLength={TEXT_LIMITS.childText}
            wrap
            onSave={(next, putBack) => {
              onReword({ key: item.key, text: next }, putBack);
            }}
          />
        </span>
      )}
      {item.note !== undefined && (
        <StatusPill tone={item.note.tone} size="sm" plain>
          {item.note.words}
        </StatusPill>
      )}
      {item.origin === "praxis" && item.source !== undefined && (
        <Source source={item.source} />
      )}
      <OriginPill origin={item.origin} surface="framework" />
      {!readOnly && (
        <IconButton
          variant="quiet"
          size="dense"
          aria-label={`Remove "${item.key}"`}
          onClick={onDrop}
        >
          <X />
        </IconButton>
      )}
    </li>
  );
}

/**
 * The child items under one subsection row, as one named list: a bold key, its words edited in
 * place, who wrote it, a Praxis item's source, a quiet remove, and a field that adds one. A
 * Definitions line has a term and words; a range ladder's rung is `keyOnly`, with a box the
 * attorney ticks to accept it and, where one applies, a note. A row holding
 * several groups draws one list for each under its own heading; this draws no heading.
 */
export function ChildLines({
  items,
  label,
  addLabel,
  placeholders,
  keyOnly = false,
  readOnly = false,
  onAdd,
  onReword,
  onDrop,
  onAccept,
}: {
  items: ChildLine[];
  /** The list's name for a reader: "Definitions", "Narrower ranges". */
  label: string;
  addLabel: string;
  /** The add field's placeholders, which also name its fields; `text` unless `keyOnly`. */
  placeholders: { key: string; text?: string };
  keyOnly?: boolean;
  readOnly?: boolean;
  onAdd: (words: ChildWords, putBack: () => void) => void;
  /** Saves an item's key and words; `putBack` shows the served words again if refused. */
  onReword: (itemId: string, words: ChildWords, putBack: () => void) => void;
  onDrop: (itemId: string) => void;
  /** Takes the attorney's yes or no to an item that needs one (`accepted` set). */
  onAccept?: (itemId: string, accepted: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <ul aria-label={label} className="space-y-1">
        {items.map((item) => (
          <Item
            key={item.item_id}
            item={item}
            keyOnly={keyOnly}
            readOnly={readOnly}
            onReword={(words, putBack) => {
              onReword(item.item_id, words, putBack);
            }}
            onDrop={() => {
              onDrop(item.item_id);
            }}
            onAccept={(accepted) => {
              onAccept?.(item.item_id, accepted);
            }}
          />
        ))}
      </ul>
      {!readOnly && (
        <AddField
          placeholder={placeholders.key}
          detail={keyOnly ? undefined : placeholders.text}
          action={addLabel}
          maxLength={TEXT_LIMITS.childKey}
          detailMaxLength={keyOnly ? undefined : TEXT_LIMITS.childText}
          onAdd={({ text: key, words: text }, putBack) => {
            onAdd(text === undefined ? { key } : { key, text }, putBack);
          }}
        />
      )}
    </div>
  );
}
